import { GreyWhite, IndigoIndigo60 } from '@sequencehq/core-components'

import { isDebugModeEnabled } from 'components/Layout/PerformanceMonitorBar/debugMode'

/**
 * Logs only when debug mode is on, so we can dig into any environment without
 * filling the console the rest of the time.
 *
 * Pass a function if building the thing you want to log is expensive - it only
 * gets called when somebody is actually watching.
 */
export const developerLog = (...args: unknown[]) => {
  if (!isDebugModeEnabled()) return

  return console.info(
    ...args.map(arg => (typeof arg === 'function' ? arg() : arg) as unknown)
  )
}

/**
 * One named section of a grouped log. The value is a function because a folded
 * group still builds everything inside it, and some of these are large.
 */
export type DeveloperLogEntry = { label: string; value: () => unknown }

/**
 * One foldable block, with a coloured `[label]` badge so it can be picked out
 * of a console that has plenty else going on.
 *
 *   [Workflow simulation] Needs review
 *     Analysis:     { ... }
 *     Dependencies: { ... }
 */
export const developerLogGroup = ({
  label,
  summary,
  entries,
  badgeBackgroundColor = IndigoIndigo60
}: {
  label: string
  /** The one line worth reading without unfolding anything. */
  summary: string
  entries: DeveloperLogEntry[]
  badgeBackgroundColor?: string
}) => {
  if (!isDebugModeEnabled()) return

  console.groupCollapsed(
    `%c[${label}]%c ${summary}`,
    badgeStyle(badgeBackgroundColor),
    SUMMARY_STYLE
  )
  entries.forEach(entry => console.log(`${entry.label}:`, entry.value()))
  console.groupEnd()
}

const badgeStyle = (backgroundColor: string) =>
  `background: ${backgroundColor}; color: ${GreyWhite}; padding: 2px 6px; border-radius: 3px; font-weight: 600;`

/** Resets the badge's styling, or it would run on into the summary. */
const SUMMARY_STYLE = 'color: inherit; background: none; font-weight: 600;'
